import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    IconFileTypePdf,
    IconLoader2,
    IconPrinter,
    IconRefresh,
    IconSchool,
    IconSearch,
    IconShare2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import educationResultService from '../../../../../services/educationResultService';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { downloadEducationBoardResultPdf } from './generateEducationBoardResultExport';
import { buildEducationBoardResultPrintHtml, getEducationBoardResultLogoUrl } from './educationBoardResultTemplate';
import {
    buildEducationBoardResultShareUrl,
    canShareEducationBoardResult,
    parseEducationBoardResultShareParams,
} from './educationBoardResultShare';

const TOOL_PATH = '/tools/result/education-board-result';

const BOARDS = [
    'barisal',
    'chittagong',
    'comilla',
    'dhaka',
    'dinajpur',
    'jessore',
    'madrasah',
    'mymensingh',
    'rajshahi',
    'sylhet',
    'tec',
];

const EXAMS = ['jsc', 'ssc', 'hsc', 'dibs'];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1995 }, (_, index) => String(currentYear - index));

const fieldClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] font-semibold text-[#3D3833] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

function normalizeNumericInput(value) {
    return String(value)
        .replace(/[০-৯]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0x09e6 + 48))
        .replace(/\D/g, '');
}

function FieldLabel({ htmlFor, children }) {
    return (
        <label htmlFor={htmlFor} className="mb-1.5 block text-[12px] font-semibold text-[#3D3833] dark:text-zinc-300">
            {children}<span className="text-red-500"> *</span>
        </label>
    );
}

function ResultInfoItem({ label, value, highlight = false }) {
    if (!value) return null;
    return (
        <div className={`rounded-xl border px-3.5 py-3 ${
            highlight
                ? 'border-orange-200 bg-orange-50 dark:border-orange-900/40 dark:bg-orange-950/20'
                : 'border-[#ECE8E4] bg-white dark:border-zinc-700 dark:bg-zinc-900'
        }`}>
            <dt className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#8C847D] dark:text-zinc-500">
                {label}
            </dt>
            <dd className={`mt-1 break-words text-[14px] font-bold ${
                highlight ? 'text-orange-800 dark:text-orange-300' : 'text-[#1C1815] dark:text-zinc-100'
            }`}>
                {value}
            </dd>
        </div>
    );
}

export default function EducationBoardResult() {
    const { t, currentLanguage } = useTranslation();
    const location = useLocation();
    const mountedRef = useRef(true);
    const exportRef = useRef(null);
    const [exporting, setExporting] = useState(false);
    const [form, setForm] = useState({
        board: '',
        exam: '',
        year: '',
        roll: '',
        reg: '',
        captcha: '',
    });
    const [sessionToken, setSessionToken] = useState('');
    const [captchaImage, setCaptchaImage] = useState('');
    const [captchaLoading, setCaptchaLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const update = (key, value) => {
        setForm((previous) => ({ ...previous, [key]: value }));
        if (result) setResult(null);
        if (error) setError('');
    };

    const loadCaptcha = useCallback(async (token = '', preserveError = false) => {
        setCaptchaLoading(true);
        if (!preserveError) setError('');
        try {
            const data = await educationResultService.getCaptcha(token);
            if (!mountedRef.current) return;
            setCaptchaImage(data.image || '');
            setSessionToken(data.sessionToken || '');
            setForm((previous) => ({ ...previous, captcha: '' }));
        } catch (requestError) {
            if (token && requestError?.code === 'RESULT_SESSION_EXPIRED') {
                await loadCaptcha('');
                return;
            }
            if (!mountedRef.current) return;
            setCaptchaImage('');
            setSessionToken('');
            // The server's own message is English-only; this one follows the page language.
            setError(t('educationBoardResult.errors.captchaLoad'));
        } finally {
            if (mountedRef.current) setCaptchaLoading(false);
        }
    }, [t]);

    useEffect(() => {
        mountedRef.current = true;
        void loadCaptcha();
        return () => {
            mountedRef.current = false;
        };
    }, [loadCaptcha]);

    useEffect(() => {
        const search = location.search;
        if (!search) return;
        const parsed = parseEducationBoardResultShareParams(search);
        if (!parsed.board && !parsed.exam && !parsed.year && !parsed.roll && !parsed.reg) return;
        setForm((previous) => ({
            ...previous,
            board: parsed.board || previous.board,
            exam: parsed.exam || previous.exam,
            year: parsed.year || previous.year,
            roll: parsed.roll || previous.roll,
            reg: parsed.reg || previous.reg,
            captcha: '',
        }));
        setResult(null);
        setError('');
    }, [location.search]);

    const hasInput = Boolean(form.board || form.exam || form.year || form.roll || form.reg || form.captcha);
    const canSubmit = Boolean(
        form.board
        && form.exam
        && form.year
        && form.roll
        && form.reg
        && form.captcha
        && sessionToken
        && !captchaLoading,
    );

    const status = deriveToolStatus({
        hasInput,
        canRun: canSubmit,
        processing: loading,
        hasResult: Boolean(result),
        hasError: Boolean(error),
    });
    const statusText = getToolStatusText(status, t, {
        ready: t('educationBoardResult.statusReady'),
        processing: t('educationBoardResult.statusProcessing'),
        success: t('educationBoardResult.statusSuccess'),
        error: t('educationBoardResult.statusError'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((step) => t(`educationBoardResult.howTo.step${step}`)),
        [t],
    );

    // The board shows a browser check before its form. A server cannot take it for the
    // visitor, so when our own captcha is unavailable the official page opens in a new
    // tab, where the visitor's own browser passes the check and shows the result.
    const openOfficialSite = () => {
        window.open('https://www.educationboardresults.gov.bd/v2/home', '_blank', 'noopener,noreferrer');
    };

    const handleSubmit = async () => {
        if (!canSubmit || loading) return;
        setLoading(true);
        setError('');
        setResult(null);
        recordToolUsage('result/education-board-result', { category: 'result' });

        try {
            const data = await educationResultService.fetchResult({
                ...form,
                sessionToken,
            });
            setResult(data);
            notifications.show({
                title: t('common.success'),
                message: t('educationBoardResult.resultFound'),
                color: 'green',
            });
            void loadCaptcha();
        } catch (requestError) {
            setError(requestError?.message || t('educationBoardResult.errors.fetch'));
            const refreshedToken = requestError?.data?.sessionToken || sessionToken;
            if (
                requestError?.code === 'CAPTCHA_FAILED'
                || requestError?.code === 'RESULT_SESSION_EXPIRED'
            ) {
                void loadCaptcha(
                    requestError?.code === 'RESULT_SESSION_EXPIRED' ? '' : refreshedToken,
                    true,
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setForm({
            board: '',
            exam: '',
            year: '',
            roll: '',
            reg: '',
            captcha: '',
        });
        setResult(null);
        setError('');
        window.history.replaceState(null, '', TOOL_PATH);
        void loadCaptcha();
    };

    const infoItems = useMemo(() => (result ? [
        ['name', result.name],
        ['roll', result.roll],
        ['registration', result.reg],
        ['fatherName', result.fatherName],
        ['motherName', result.motherName],
        ['board', result.board],
        ['exam', result.examType],
        ['group', result.group],
        ['institute', result.institute],
        ['dateOfBirth', result.dateOfBirth],
        ['gpa', result.gpa],
    ] : []), [result]);

    const exportLabels = useMemo(() => ({
        title: t('educationBoardResult.title'),
        documentTagline: t('educationBoardResult.documentTagline'),
        documentDisclaimer: t('educationBoardResult.documentDisclaimer'),
        resultStatus: t('educationBoardResult.resultStatus'),
        published: t('educationBoardResult.published'),
        studentDetails: t('educationBoardResult.studentDetails'),
        subjectGrades: t('educationBoardResult.subjectGrades'),
        officialSource: t('educationBoardResult.officialSource'),
        fields: {
            name: t('educationBoardResult.fields.name'),
            roll: t('educationBoardResult.fields.roll'),
            registration: t('educationBoardResult.fields.registration'),
            fatherName: t('educationBoardResult.fields.fatherName'),
            motherName: t('educationBoardResult.fields.motherName'),
            board: t('educationBoardResult.fields.board'),
            exam: t('educationBoardResult.fields.exam'),
            group: t('educationBoardResult.fields.group'),
            institute: t('educationBoardResult.fields.institute'),
            dateOfBirth: t('educationBoardResult.fields.dateOfBirth'),
            gpa: t('educationBoardResult.fields.gpa'),
        },
        gradeTable: {
            code: t('educationBoardResult.gradeTable.code'),
            subject: t('educationBoardResult.gradeTable.subject'),
            grade: t('educationBoardResult.gradeTable.grade'),
        },
    }), [t]);

    const exportLogoUrl = useMemo(
        () => getEducationBoardResultLogoUrl(window.location.origin, currentLanguage),
        [currentLanguage],
    );

    const handleDownloadPdf = async () => {
        if (!result) return;
        setExporting(true);
        try {
            await downloadEducationBoardResultPdf(
                {
                    result,
                    infoItems,
                    labels: exportLabels,
                    logoUrl: exportLogoUrl,
                    language: currentLanguage,
                },
                `sohozkaj-education-result-${Date.now()}.pdf`,
            );
            notifications.show({
                title: t('common.success'),
                message: t('educationBoardResult.pdfDownloaded'),
                color: 'green',
            });
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('educationBoardResult.exportFailed'),
                color: 'red',
            });
        } finally {
            setExporting(false);
        }
    };

    const handlePrint = useCallback(() => {
        if (!result) return;

        recordToolUsage('result/education-board-result', { category: 'result' });
        const html = buildEducationBoardResultPrintHtml({
            result,
            infoItems,
            labels: exportLabels,
            logoUrl: exportLogoUrl,
        });

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDoc = frameWindow?.document;
        if (!frameDoc || !frameWindow) {
            iframe.remove();
            return;
        }

        frameDoc.open();
        frameDoc.write(html);
        frameDoc.close();

        const cleanup = () => iframe.remove();
        frameWindow.addEventListener('afterprint', cleanup, { once: true });

        let printed = false;
        const triggerPrint = () => {
            if (printed) return;
            printed = true;
            frameWindow.focus();
            frameWindow.print();
            setTimeout(cleanup, 60000);
        };

        const waitForAssets = async () => {
            try {
                await frameDoc.fonts?.ready;
            } catch {
                // ignore font load errors
            }
            const images = Array.from(frameDoc.images || []);
            await Promise.all(images.map((img) => {
                if (img.complete) return Promise.resolve();
                return new Promise((resolve) => {
                    img.onload = resolve;
                    img.onerror = resolve;
                });
            }));
        };

        void waitForAssets().then(triggerPrint);
        setTimeout(triggerPrint, 1500);
    }, [result, infoItems, exportLabels, exportLogoUrl]);

    const handleShare = async () => {
        if (!canShareEducationBoardResult(form)) {
            notifications.show({
                title: t('common.error'),
                message: t('educationBoardResult.shareRequiresInput'),
                color: 'yellow',
            });
            return;
        }

        const url = buildEducationBoardResultShareUrl(window.location.origin, TOOL_PATH, {
            board: form.board,
            exam: form.exam,
            year: form.year,
            roll: form.roll,
            reg: form.reg,
        });
        const copied = copyTextToClipboard(url);

        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({
                    title: t('educationBoardResult.shareTitle'),
                    text: t('educationBoardResult.shareText'),
                    url,
                });
                notifications.show({
                    title: t('common.success'),
                    message: t('educationBoardResult.shared'),
                    color: 'green',
                });
                return;
            } catch (err) {
                if (err?.name === 'AbortError') return;
            }
        }

        if (copied) {
            notifications.show({
                title: t('common.success'),
                message: t('educationBoardResult.linkCopied'),
                color: 'green',
            });
        } else {
            notifications.show({
                title: t('common.error'),
                message: t('educationBoardResult.shareFailed'),
                color: 'red',
            });
        }
    };

    return (
        <ToolPageLayout
            slug="result/education-board-result"
            subtitleKey="educationBoardResult.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('educationBoardResult.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasInput || result ? handleReset : undefined}
            resetLabel={t('educationBoardResult.reset')}
            showTrust={false}
            primaryAction={{
                label: loading ? t('educationBoardResult.fetching') : t('educationBoardResult.getResult'),
                onClick: handleSubmit,
                disabled: !canSubmit || loading,
                loading,
                icon: IconSearch,
            }}
            controls={(
                <div className="space-y-4">
                    <p className="text-[12.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('educationBoardResult.settingsHint')}
                    </p>

                    <div>
                        <FieldLabel htmlFor="result-board">{t('educationBoardResult.fields.board')}</FieldLabel>
                        <select
                            id="result-board"
                            value={form.board}
                            onChange={(event) => update('board', event.target.value)}
                            className={fieldClass}
                        >
                            <option value="">
                                {t('educationBoardResult.placeholders.board')}
                            </option>
                            {BOARDS.map((board) => (
                                <option key={board} value={board}>
                                    {t(`educationBoardResult.boards.${board}`)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <FieldLabel htmlFor="result-exam">{t('educationBoardResult.fields.exam')}</FieldLabel>
                        <select
                            id="result-exam"
                            value={form.exam}
                            onChange={(event) => update('exam', event.target.value)}
                            className={fieldClass}
                        >
                            <option value="">
                                {t('educationBoardResult.placeholders.exam')}
                            </option>
                            {EXAMS.map((exam) => (
                                <option key={exam} value={exam}>
                                    {t(`educationBoardResult.exams.${exam}`)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <FieldLabel htmlFor="result-year">{t('educationBoardResult.fields.year')}</FieldLabel>
                        <select
                            id="result-year"
                            value={form.year}
                            onChange={(event) => update('year', event.target.value)}
                            className={fieldClass}
                        >
                            <option value="">
                                {t('educationBoardResult.placeholders.year')}
                            </option>
                            {YEARS.map((year) => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </div>

                    <div>
                        <FieldLabel htmlFor="result-roll">{t('educationBoardResult.fields.roll')}</FieldLabel>
                        <input
                            id="result-roll"
                            type="text"
                            inputMode="numeric"
                            value={form.roll}
                            onChange={(event) => update('roll', normalizeNumericInput(event.target.value))}
                            placeholder={t('educationBoardResult.placeholders.roll')}
                            className={fieldClass}
                        />
                    </div>

                    <div>
                        <FieldLabel htmlFor="result-reg">{t('educationBoardResult.fields.registration')}</FieldLabel>
                        <input
                            id="result-reg"
                            type="text"
                            inputMode="numeric"
                            value={form.reg}
                            onChange={(event) => update('reg', normalizeNumericInput(event.target.value))}
                            placeholder={t('educationBoardResult.placeholders.registration')}
                            className={fieldClass}
                        />
                    </div>

                    <div className="rounded-xl border border-[#ECE8E4] bg-[#F7F7F9] p-3 dark:border-zinc-700 dark:bg-zinc-800/70">
                        <FieldLabel htmlFor="result-captcha">{t('educationBoardResult.fields.captcha')}</FieldLabel>
                        <div className="relative mb-3 mt-1 pr-2 pt-2">
                            <button
                                type="button"
                                onClick={() => void loadCaptcha(sessionToken)}
                                disabled={captchaLoading || loading}
                                aria-label={t('educationBoardResult.reloadCaptcha')}
                                title={t('educationBoardResult.reloadCaptcha')}
                                className="absolute -right-0.5 -top-0.5 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-[#DDD7D1] bg-white text-[#3D3833] shadow-sm transition-colors hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconRefresh size={16} stroke={2} />
                            </button>
                            <div className="flex min-h-[104px] w-full items-center justify-center overflow-hidden rounded-xl border border-[#DDD7D1] bg-white px-3 py-4 dark:border-zinc-600 dark:bg-zinc-900">
                                {captchaLoading ? (
                                    <span className="text-[12px] font-medium text-[#8C847D] dark:text-zinc-500">
                                        {t('educationBoardResult.captchaLoading')}
                                    </span>
                                ) : captchaImage ? (
                                    <img
                                        src={captchaImage}
                                        alt={t('educationBoardResult.captchaAlt')}
                                        className="h-auto max-h-[88px] w-full object-contain"
                                    />
                                ) : (
                                    <div className="flex flex-col items-center gap-2 px-2 text-center">
                                        <span className="text-[12px] text-red-600">
                                            {t('educationBoardResult.captchaUnavailable')}
                                        </span>
                                        <span className="text-[11.5px] leading-snug text-[#6B635C] dark:text-zinc-400">
                                            {t('educationBoardResult.officialSite.body')}
                                        </span>
                                        {/* The board's own check runs in the visitor's browser here,
                                            so it is theirs to pass — the result then shows on the
                                            official page itself. */}
                                        <button
                                            type="button"
                                            onClick={openOfficialSite}
                                            className="rounded-lg bg-primary px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-primary/90"
                                        >
                                            {t('educationBoardResult.officialSite.open')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <input
                            id="result-captcha"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={form.captcha}
                            onChange={(event) => update('captcha', normalizeNumericInput(event.target.value).slice(0, 8))}
                            placeholder={t('educationBoardResult.placeholders.captcha')}
                            className={fieldClass}
                        />
                    </div>

                    <p className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/20 dark:text-orange-300">
                        {t('educationBoardResult.sourceNote')}
                    </p>

                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit || loading}
                        className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl text-[14.5px] font-semibold transition-colors ${
                            !canSubmit || loading
                                ? 'cursor-not-allowed bg-[#F7F5F3] text-[#A8A099] dark:bg-zinc-800 dark:text-zinc-600'
                                : 'bg-primary text-white hover:bg-primary/90'
                        }`}
                    >
                        {loading ? (
                            <IconLoader2 size={18} className="animate-spin" />
                        ) : (
                            <IconSearch size={18} stroke={1.9} />
                        )}
                        {loading ? t('educationBoardResult.fetching') : t('educationBoardResult.getResult')}
                    </button>
                </div>
            )}
            settings={result ? (
                <div className="space-y-3">
                    <button
                        type="button"
                        onClick={handleReset}
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-600 transition-colors hover:bg-red-100 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400"
                    >
                        <IconRefresh size={14} />
                        {t('educationBoardResult.reset')}
                    </button>
                    <ToolCard title={t('educationBoardResult.exportShareTitle')}>
                        <div className="grid grid-cols-3 gap-1.5">
                            <button
                                type="button"
                                onClick={handlePrint}
                                disabled={exporting}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconPrinter size={15} className="text-primary" stroke={1.8} />
                                {t('educationBoardResult.print')}
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadPdf}
                                disabled={exporting}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconFileTypePdf size={15} className="text-primary" stroke={1.8} />
                                {exporting ? t('educationBoardResult.downloading') : t('educationBoardResult.downloadPdf')}
                            </button>
                            <button
                                type="button"
                                onClick={handleShare}
                                disabled={exporting}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconShare2 size={15} className="text-primary" stroke={1.8} />
                                {t('educationBoardResult.share')}
                            </button>
                        </div>
                    </ToolCard>
                </div>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('educationBoardResult.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('educationBoardResult.previewEmpty')}
                    idleHint={t('educationBoardResult.previewEmptyHint')}
                    processingLabel={t('educationBoardResult.fetching')}
                    error={error}
                    showIdle={!result && !error}
                    fill
                    flushContent={Boolean(result)}
                >
                    {result ? (
                        <div
                            ref={exportRef}
                            data-education-board-export
                            className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl bg-[#F7F5F3] p-3 shadow-[inset_0_0_0_1px_#ECE8E4] dark:bg-zinc-800/60 dark:shadow-[inset_0_0_0_1px_theme(colors.zinc.700)] sm:p-4"
                        >
                            <div className="space-y-4">
                                <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 text-center dark:border-orange-900/40 dark:bg-orange-950/20">
                                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-white text-primary shadow-sm dark:bg-zinc-900">
                                        <IconSchool size={25} stroke={1.7} />
                                    </div>
                                    <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[#6B635C] dark:text-zinc-400">
                                        {t('educationBoardResult.resultStatus')}
                                    </p>
                                    <p className="mt-1 text-[24px] font-black text-orange-800 dark:text-orange-300">
                                        {result.result || t('educationBoardResult.published')}
                                    </p>
                                    {result.gpa ? (
                                        <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-3 py-1 text-[12px] font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                            {t('educationBoardResult.fields.gpa')}: {result.gpa}
                                        </span>
                                    ) : null}
                                </div>

                                <div>
                                    <h3 className="mb-2.5 text-[13px] font-bold text-[#3D3833] dark:text-zinc-200">
                                        {t('educationBoardResult.studentDetails')}
                                    </h3>
                                    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        {infoItems.map(([key, value]) => (
                                            <ResultInfoItem
                                                key={key}
                                                label={t(`educationBoardResult.fields.${key}`)}
                                                value={value}
                                                highlight={key === 'gpa'}
                                            />
                                        ))}
                                    </dl>
                                </div>

                                {Array.isArray(result.grades) && result.grades.length ? (
                                    <div>
                                        <h3 className="mb-2.5 text-[13px] font-bold text-[#3D3833] dark:text-zinc-200">
                                            {t('educationBoardResult.subjectGrades')}
                                        </h3>
                                        <div className="overflow-x-auto rounded-xl border border-[#ECE8E4] dark:border-zinc-700">
                                            <table className="w-full min-w-[390px] border-collapse text-[12px]">
                                                <thead>
                                                    <tr className="bg-orange-50 dark:bg-orange-950/20">
                                                        <th className="px-3 py-2.5 text-left font-semibold text-orange-800 dark:text-orange-300">{t('educationBoardResult.gradeTable.code')}</th>
                                                        <th className="px-3 py-2.5 text-left font-semibold text-orange-800 dark:text-orange-300">{t('educationBoardResult.gradeTable.subject')}</th>
                                                        <th className="px-3 py-2.5 text-center font-semibold text-orange-800 dark:text-orange-300">{t('educationBoardResult.gradeTable.grade')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {result.grades.map((grade, index) => (
                                                        <tr key={`${grade.code}-${index}`} className={index % 2 ? 'bg-[#FAFAFC] dark:bg-zinc-800/50' : 'bg-white dark:bg-zinc-900'}>
                                                            <td className="border-t border-[#ECE8E4] px-3 py-2.5 font-semibold dark:border-zinc-700">{grade.code || '—'}</td>
                                                            <td className="border-t border-[#ECE8E4] px-3 py-2.5 dark:border-zinc-700">{grade.subject || '—'}</td>
                                                            <td className="border-t border-[#ECE8E4] px-3 py-2.5 text-center font-bold text-primary dark:border-zinc-700">{grade.grade || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <p className="mt-auto pt-4 text-center text-[11px] text-[#9CA3AF] dark:text-zinc-600">
                                {t('educationBoardResult.officialSource')}
                            </p>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
